package com.sddamico.components.widgets

import androidx.compose.runtime.*
import com.sddamico.SitePalette
import com.sddamico.components.motion.Collapsible
import com.sddamico.components.motion.PressableStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.sddamico.api.SuccessResponse
import com.sddamico.api.apiGet
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.models.PushSubscribeRequest
import com.sddamico.models.VapidKeyResponse
import com.sddamico.push.getPermissionState
import com.sddamico.push.isPushSupported
import com.sddamico.push.subscribeToPush
import com.sddamico.state.LocalAuthState
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.localStorage
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private const val DISMISSED_KEY = "fcc:push_dismissed"

@Composable
fun PushNotificationBanner() {
    val authState = LocalAuthState.current
    val scope = rememberCoroutineScope()

    var visible by remember { mutableStateOf(false) }
    var enabling by remember { mutableStateOf(false) }

    if (authState.user?.isImpersonating == true) return

    LaunchedEffect(authState.user) {
        if (authState.user == null || !isPushSupported()
            || getPermissionState() != "default"
            || localStorage.getItem(DISMISSED_KEY) != null
        ) {
            visible = false
            return@LaunchedEffect
        }
        // Only show if server has VAPID keys configured
        visible = try {
            apiGet("/api/push/vapid-key", VapidKeyResponse.serializer())
            true
        } catch (_: Exception) {
            false
        }
    }

    // Slides open rather than shoving the page down when the async eligibility check lands, and closes the same way.
    Collapsible(visible) {
        Row(
            Modifier
                .fillMaxWidth()
                .backgroundColor(SitePalette.surface)
                .border(1.px, LineStyle.Solid, SitePalette.border)
                .borderRadius(8.px)
                .padding(topBottom = 0.625.cssRem, leftRight = 1.cssRem)
                .margin(bottom = 1.cssRem)
                .gap(0.75.cssRem),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                "Get notified when tournaments finish",
                Modifier
                    .fontSize(0.8125.cssRem)
                    .color(SitePalette.text)
            )

            Spacer()

            // "Not now" button
            SpanText(
                "Not now",
                Modifier
                    .fontSize(0.75.cssRem)
                    .color(SitePalette.textMuted)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        localStorage.setItem(DISMISSED_KEY, "1")
                        visible = false
                    }
            )

            // "Enable" button
            Div(
                PressableStyle.toModifier()
                    .backgroundColor(if (enabling) SitePalette.border else SitePalette.primary)
                    .color(Colors.White)
                    .borderRadius(6.px)
                    .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
                    .fontSize(0.75.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .cursor(if (enabling) Cursor.Default else Cursor.Pointer)
                    .onClick {
                        if (enabling) return@onClick
                        scope.launch {
                            enabling = true
                            try {
                                val vapidKey = apiGet("/api/push/vapid-key", VapidKeyResponse.serializer())
                                val sub = subscribeToPush(vapidKey.publicKey)
                                if (sub != null) {
                                    val body = apiJson.encodeToString(PushSubscribeRequest(sub.endpoint, sub.p256dh, sub.auth))
                                    apiPost("/api/push/subscribe", body, SuccessResponse.serializer())
                                }
                            } catch (_: Exception) {
                                // Permission denied or error — either way, hide banner
                            }
                            visible = false
                        }
                    }
                    .toAttrs()
            ) {
                Text(if (enabling) "Enabling..." else "Enable")
            }
        }
    }
}
